/**
 * Audit Query Hooks
 *
 * Feature: 123-audit-log-viewer
 * Description: React Query hooks for audit log viewer
 * Constitution: v1.12.0
 */

import { useQuery } from '@tanstack/react-query';
import * as auditViewerApi from '../services/auditViewerApi';
import type {
  SystemAuditLogFilters,
  AuthAuditLogFilters,
  PaginationParams,
} from '../types/audit';

const auditKeys = {
  all: ['audit'] as const,
  systemLog: (filters: SystemAuditLogFilters, pagination: PaginationParams) =>
    [...auditKeys.all, 'system', filters, pagination] as const,
  authLog: (filters: AuthAuditLogFilters, pagination: PaginationParams) =>
    [...auditKeys.all, 'auth', filters, pagination] as const,
  entityTypes: () => [...auditKeys.all, 'entity-types'] as const,
  actions: () => [...auditKeys.all, 'actions'] as const,
};

export function useSystemAuditLog(
  filters: SystemAuditLogFilters | null,
  pagination: PaginationParams
) {
  return useQuery({
    queryKey: auditKeys.systemLog(filters!, pagination),
    queryFn: () => auditViewerApi.getSystemAuditLog(filters!, pagination),
    enabled: !!filters,
  });
}

export function useAuthAuditLog(
  filters: AuthAuditLogFilters | null,
  pagination: PaginationParams
) {
  return useQuery({
    queryKey: auditKeys.authLog(filters!, pagination),
    queryFn: () => auditViewerApi.getAuthAuditLog(filters!, pagination),
    enabled: !!filters,
  });
}

export function useEntityTypes() {
  return useQuery({
    queryKey: auditKeys.entityTypes(),
    queryFn: auditViewerApi.getEntityTypes,
    staleTime: 5 * 60 * 1000, // Entity types don't change often
  });
}

export function useActions() {
  return useQuery({
    queryKey: auditKeys.actions(),
    queryFn: auditViewerApi.getActions,
    staleTime: 5 * 60 * 1000,
  });
}
